/**
 * Party — wallet, budget pools, loot.
 *
 * Read path: fetches the party snapshot + loot list + audit log via the
 * SessionRead HTTP lambda. Live updates: opens a CampaignSocket and listens
 * for party/patch broadcasts; wallet edits ship a party/update mutation
 * carrying the FULL new doc under a version check (see partyProtocol.ts).
 *
 * The page is intentionally dumb about shape: it defensive-renders whatever
 * the API returns, so a partially-seeded party or a pool with missing
 * optional fields won't crash the view.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AuditEntry,
  DEFAULT_SESSION_ID,
  getAuditLog,
  getPartySnapshot,
  PartySnapshot,
} from '../lib/sessionApi';
import {
  BudgetPool,
  LootItem,
  Party as PartyDoc,
  Wallet,
  ZERO_WALLET,
  addWallets,
  charScope,
  ownerScopeLabel,
  subWallets,
  unallocatedWallet,
  walletCovers,
} from '../lib/party';
import { getCharacters } from '../lib/sessionApi';
import { CampaignSocket } from '../lib/campaignSocket';
import {
  PartyActions,
  PartyPatchPayload,
  PartyRejectedPayload,
  PartyServerTypes,
  PartyUpdateRequest,
} from '../lib/partyProtocol';
import {
  LootActions,
  LootAddRequest,
  LootRejectedPayload,
  LootRemovedPayload,
  LootRemoveRequest,
  LootRowPayload,
  LootServerTypes,
  LootUpdateRequest,
} from '../lib/lootProtocol';

// Match Sheet.tsx — both pages subscribe to the same single-campaign map so
// they end up on the same WebSocket fan-out group.
const DEFAULT_MAP_ID = 'local-dev';

/**
 * Render a wallet as the four coin columns, muted when zero. Uses the
 * JetBrains Mono stack via the .num class from the design system.
 */
function WalletLine({ wallet }: { wallet: Wallet | undefined }) {
  const w = wallet ?? { cp: 0, sp: 0, gp: 0, pp: 0 };
  const coins: Array<[keyof Wallet, string]> = [
    ['pp', 'pp'],
    ['gp', 'gp'],
    ['sp', 'sp'],
    ['cp', 'cp'],
  ];
  return (
    <span className="num" style={{ display: 'inline-flex', gap: '0.75rem' }}>
      {coins.map(([k, label]) => (
        <span
          key={k}
          style={{
            color: w[k] > 0 ? 'var(--fg)' : 'var(--fg-muted)',
          }}
        >
          {w[k]} <span style={{ color: 'var(--fg-muted)' }}>{label}</span>
        </span>
      ))}
    </span>
  );
}

function PoolRow({
  pool,
  onEdit,
  onFund,
  onSpend,
  onArchiveToggle,
  onDelete,
}: {
  pool: BudgetPool;
  onEdit: (id: string) => void;
  onFund: (id: string) => void;
  onSpend: (id: string) => void;
  onArchiveToggle: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const scope = ownerScopeLabel(pool.ownerScope);
  return (
    <div
      style={{
        padding: '0.75rem 1rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        marginBottom: '0.5rem',
        opacity: pool.archived ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{pool.name}</strong>
        <span style={{ fontSize: '0.85rem', color: 'var(--fg-muted)' }}>{scope}</span>
      </div>
      {pool.description && (
        <p style={{ margin: '0.25rem 0', color: 'var(--fg-muted)', fontSize: '0.9rem' }}>
          {pool.description}
        </p>
      )}
      <div style={{ marginTop: '0.25rem' }}>
        <WalletLine wallet={pool.currentAmount} />
        {pool.targetAmount && (
          <span style={{ marginLeft: '0.75rem', color: 'var(--fg-muted)' }}>
            / <WalletLine wallet={pool.targetAmount} />
          </span>
        )}
      </div>
      <div
        style={{
          marginTop: '0.5rem',
          display: 'flex',
          gap: '0.25rem',
          flexWrap: 'wrap',
          fontSize: '0.85rem',
        }}
      >
        {!pool.archived && (
          <>
            <button onClick={() => onFund(pool.id)}>Fund</button>
            <button onClick={() => onSpend(pool.id)}>Spend</button>
          </>
        )}
        <button onClick={() => onEdit(pool.id)}>Edit</button>
        <button onClick={() => onArchiveToggle(pool.id)}>
          {pool.archived ? 'Unarchive' : 'Archive'}
        </button>
        <button
          onClick={() => {
            if (
              window.confirm(
                `Delete pool '${pool.name}'? Its balance returns to unallocated.`,
              )
            )
              onDelete(pool.id);
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function LootRow({
  item,
  onEdit,
  onRemove,
}: {
  item: LootItem;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const display = item.identified ? item.name : `Unknown ${(item.tags?.[0]) ?? 'item'}`;
  return (
    <tr>
      <td>{display}</td>
      <td className="num" style={{ textAlign: 'right' }}>{item.quantity}</td>
      <td style={{ color: 'var(--fg-muted)' }}>{item.source ?? ''}</td>
      <td style={{ color: 'var(--fg-muted)' }}>{item.tags?.join(', ') ?? ''}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button onClick={() => onEdit(item.id)} style={{ marginRight: '0.25rem' }}>
          Edit
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Remove ${item.name}?`)) onRemove(item.id);
          }}
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

/**
 * Shared form for add + edit. The add case passes an empty `initial` prop
 * and `onSubmit` wires a LootAddRequest; the edit case passes the loot
 * row's current fields and wires a LootUpdateRequest. Keeping them the same
 * form means there's only one place to change when we add a new field to
 * LootItem.
 *
 * Tags input is a comma-separated string on the way in and out — the array
 * roundtrip is done here so the form stays a dumb controlled component.
 */
function LootForm({
  mode,
  initial,
  onSubmit,
  onCancel,
}: {
  mode: 'add' | 'edit';
  initial?: LootItem;
  onSubmit: (fields: {
    name: string;
    quantity: number;
    weight?: number;
    notes?: string;
    source?: string;
    identified: boolean;
    tags?: string[];
    reason?: string;
  }) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [quantity, setQuantity] = useState(initial?.quantity ?? 1);
  const [weight, setWeight] = useState<string>(
    initial?.weight != null ? String(initial.weight) : '',
  );
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [source, setSource] = useState(initial?.source ?? '');
  const [identified, setIdentified] = useState(initial?.identified ?? true);
  const [tags, setTags] = useState((initial?.tags ?? []).join(', '));
  const [reason, setReason] = useState('');

  const submit = () => {
    const trimmedName = name.trim();
    if (!trimmedName || quantity < 1) return;
    const parsedWeight = weight.trim() === '' ? undefined : Number(weight);
    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    onSubmit({
      name: trimmedName,
      quantity,
      weight: Number.isFinite(parsedWeight) ? parsedWeight : undefined,
      notes: notes.trim() || undefined,
      source: source.trim() || undefined,
      identified,
      tags: parsedTags.length > 0 ? parsedTags : undefined,
      reason: reason.trim() || undefined,
    });
    if (mode === 'add') {
      setName('');
      setQuantity(1);
      setWeight('');
      setNotes('');
      setSource('');
      setIdentified(true);
      setTags('');
      setReason('');
    }
  };

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
        gap: '0.5rem 0.75rem',
        alignItems: 'flex-end',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: 'span 2' }}>
        <span style={{ color: 'var(--fg-muted)' }}>name</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--fg-muted)' }}>qty</span>
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
          className="num"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--fg-muted)' }}>weight (lb)</span>
        <input
          type="number"
          step="0.1"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          className="num"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--fg-muted)' }}>source</span>
        <input type="text" value={source} onChange={(e) => setSource(e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: 'span 2' }}>
        <span style={{ color: 'var(--fg-muted)' }}>tags (comma-separated)</span>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="magic, consumable"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: 'span 2' }}>
        <span style={{ color: 'var(--fg-muted)' }}>notes</span>
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
        <input
          type="checkbox"
          checked={identified}
          onChange={(e) => setIdentified(e.target.checked)}
        />
        <span>identified</span>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: 'span 2' }}>
        <span style={{ color: 'var(--fg-muted)' }}>reason (optional)</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={mode === 'add' ? 'e.g. loot from goblin chest' : 'e.g. corrected qty'}
        />
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', gridColumn: '1 / -1' }}>
        {onCancel && <button onClick={onCancel}>Cancel</button>}
        <button onClick={submit} disabled={!name.trim() || quantity < 1}>
          {mode === 'add' ? 'Add' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * Inline wallet deposit/withdraw form. Lets the user type a coin amount per
 * column and apply it as a signed delta against the current wallet — negative
 * values withdraw, positive values deposit. We submit the FULL new wallet
 * because that's the on-wire shape (party/update is a full-replace), but the
 * UI is delta-shaped because that's how players think about it ("got 50gp
 * from the chest", "spent 3sp at the inn").
 */
function WalletEditor({
  wallet,
  onSubmit,
}: {
  wallet: Wallet;
  onSubmit: (next: Wallet, reason?: string) => void;
}) {
  const [delta, setDelta] = useState<Wallet>({ cp: 0, sp: 0, gp: 0, pp: 0 });
  const [reason, setReason] = useState('');

  const apply = () => {
    const next: Wallet = {
      cp: wallet.cp + delta.cp,
      sp: wallet.sp + delta.sp,
      gp: wallet.gp + delta.gp,
      pp: wallet.pp + delta.pp,
    };
    if (next.cp < 0 || next.sp < 0 || next.gp < 0 || next.pp < 0) {
      // Local guard so we don't even ship a request the server would reject.
      return;
    }
    onSubmit(next, reason.trim() || undefined);
    setDelta({ cp: 0, sp: 0, gp: 0, pp: 0 });
    setReason('');
  };

  const allZero = delta.cp === 0 && delta.sp === 0 && delta.gp === 0 && delta.pp === 0;
  const coins: Array<keyof Wallet> = ['pp', 'gp', 'sp', 'cp'];

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
      }}
    >
      {coins.map((c) => (
        <label key={c} style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>{c}</span>
          <input
            type="number"
            value={delta[c]}
            onChange={(e) =>
              setDelta((prev) => ({ ...prev, [c]: Number(e.target.value) || 0 }))
            }
            className="num"
            style={{ width: '5rem' }}
          />
        </label>
      ))}
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', flex: 1, minWidth: '12rem' }}>
        <span style={{ color: 'var(--fg-muted)' }}>reason (optional)</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. chest in goblin cave"
        />
      </label>
      <button onClick={apply} disabled={allZero}>
        Apply
      </button>
    </div>
  );
}

/**
 * Shared metadata form for pool create + edit. Name is required; description
 * and target are optional. Target is all four coins so the player can spec a
 * mixed goal ("save 50gp and 20pp"). The form only edits pool METADATA —
 * currentAmount is moved through the separate Fund/Spend controls.
 */
function PoolMetaForm({
  mode,
  initial,
  characters,
  onSubmit,
  onCancel,
}: {
  mode: 'create' | 'edit';
  initial?: BudgetPool;
  /** Character roster for the scope picker. Empty array hides the picker. */
  characters: Array<{ id: string; name: string }>;
  onSubmit: (fields: {
    name: string;
    description?: string;
    targetAmount?: Wallet;
    ownerScope: string;
  }) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [scopeValue, setScopeValue] = useState(initial?.ownerScope ?? 'party');
  const [target, setTarget] = useState<Wallet>(
    initial?.targetAmount ?? { cp: 0, sp: 0, gp: 0, pp: 0 },
  );
  const [useTarget, setUseTarget] = useState(initial?.targetAmount != null);

  const coins: Array<keyof Wallet> = ['pp', 'gp', 'sp', 'cp'];

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const hasTargetValue =
      useTarget &&
      (target.cp > 0 || target.sp > 0 || target.gp > 0 || target.pp > 0);
    onSubmit({
      name: trimmed,
      description: description.trim() || undefined,
      targetAmount: hasTargetValue ? target : undefined,
      ownerScope: scopeValue,
    });
    if (mode === 'create') {
      setName('');
      setDescription('');
      setTarget({ cp: 0, sp: 0, gp: 0, pp: 0 });
      setUseTarget(false);
      setScopeValue('party');
    }
  };

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
        gap: '0.5rem 0.75rem',
        alignItems: 'flex-end',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: 'span 2' }}>
        <span style={{ color: 'var(--fg-muted)' }}>name</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
        <span style={{ color: 'var(--fg-muted)' }}>description (optional)</span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. saving for airship repairs"
        />
      </label>
      {characters.length > 0 && (
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
          <span style={{ color: 'var(--fg-muted)' }}>owner</span>
          <select
            value={scopeValue}
            onChange={(e) => setScopeValue(e.target.value)}
            style={{ maxWidth: '18rem' }}
          >
            <option value="party">Party (anyone can spend)</option>
            {characters.map((ch) => (
              <option key={ch.id} value={charScope(ch.id)}>
                {ch.name} (only owner + DM)
              </option>
            ))}
          </select>
        </label>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
        <input
          type="checkbox"
          checked={useTarget}
          onChange={(e) => setUseTarget(e.target.checked)}
        />
        <span>has savings target</span>
      </label>
      {useTarget &&
        coins.map((c) => (
          <label key={c} style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--fg-muted)' }}>target {c}</span>
            <input
              type="number"
              min={0}
              value={target[c]}
              onChange={(e) =>
                setTarget((prev) => ({
                  ...prev,
                  [c]: Math.max(0, Number(e.target.value) || 0),
                }))
              }
              className="num"
            />
          </label>
        ))}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', gridColumn: '1 / -1' }}>
        {onCancel && <button onClick={onCancel}>Cancel</button>}
        <button onClick={submit} disabled={!name.trim()}>
          {mode === 'create' ? 'Create' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * Inline coin-picker used for Fund (move from unallocated into pool) and
 * Spend (move out of the pool, money leaves the party). Caller supplies the
 * verb and the local guard so this component stays a dumb controlled form.
 */
function PoolCoinForm({
  verb,
  onSubmit,
  onCancel,
}: {
  verb: 'Fund' | 'Spend';
  onSubmit: (amount: Wallet, reason?: string) => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState<Wallet>({ cp: 0, sp: 0, gp: 0, pp: 0 });
  const [reason, setReason] = useState('');
  const coins: Array<keyof Wallet> = ['pp', 'gp', 'sp', 'cp'];
  const allZero =
    amount.cp === 0 && amount.sp === 0 && amount.gp === 0 && amount.pp === 0;

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        border: '1px dashed var(--border)',
        borderRadius: '6px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
      }}
    >
      {coins.map((c) => (
        <label key={c} style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>{c}</span>
          <input
            type="number"
            min={0}
            value={amount[c]}
            onChange={(e) =>
              setAmount((prev) => ({
                ...prev,
                [c]: Math.max(0, Number(e.target.value) || 0),
              }))
            }
            className="num"
            style={{ width: '5rem' }}
          />
        </label>
      ))}
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', flex: 1, minWidth: '12rem' }}>
        <span style={{ color: 'var(--fg-muted)' }}>reason (optional)</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={verb === 'Fund' ? 'e.g. set aside for the ship' : 'e.g. paid the smith'}
        />
      </label>
      <button onClick={onCancel}>Cancel</button>
      <button onClick={() => onSubmit(amount, reason.trim() || undefined)} disabled={allZero}>
        {verb}
      </button>
    </div>
  );
}

export default function Party() {
  const [snapshot, setSnapshot] = useState<PartySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [lootError, setLootError] = useState<string | null>(null);
  const [editingLootId, setEditingLootId] = useState<string | null>(null);
  // Pool UI modes. Only one affordance is open at a time across all pools —
  // clicking Fund on pool A while pool B's edit form is open closes pool B.
  const [creatingPool, setCreatingPool] = useState(false);
  const [editingPoolId, setEditingPoolId] = useState<string | null>(null);
  const [fundingPoolId, setFundingPoolId] = useState<string | null>(null);
  const [spendingPoolId, setSpendingPoolId] = useState<string | null>(null);
  const closePoolForms = () => {
    setCreatingPool(false);
    setEditingPoolId(null);
    setFundingPoolId(null);
    setSpendingPoolId(null);
  };
  // Character roster for the pool scope picker. Loaded once on mount.
  const [charRoster, setCharRoster] = useState<Array<{ id: string; name: string }>>([]);
  // Sub → display name map for resolving UUIDs in audit entries to human-readable names.
  const [playerNames, setPlayerNames] = useState<Map<string, string>>(new Map());
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditCursor, setAuditCursor] = useState<string | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  const socketRef = useRef<CampaignSocket | null>(null);
  // Tracks the version we expect the next server patch to carry. Optimistic
  // sends bump this; rejections snap it back to whatever the server says.
  const pendingVersionRef = useRef<number>(0);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await getPartySnapshot(DEFAULT_SESSION_ID);
      setSnapshot(data);
      pendingVersionRef.current = data.party.version;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load party');
    } finally {
      setLoading(false);
    }
  }, []);

  // Audit log loads independently of the party snapshot — it's a separate
  // partition read and a slow audit endpoint shouldn't block the party view
  // from rendering.
  const loadInitialAudit = useCallback(async () => {
    setAuditError(null);
    setAuditLoading(true);
    try {
      const page = await getAuditLog(DEFAULT_SESSION_ID, { limit: 50 });
      setAudit(page.entries);
      setAuditCursor(page.nextBefore);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadOlderAudit = useCallback(async () => {
    if (!auditCursor) return;
    setAuditLoading(true);
    try {
      const page = await getAuditLog(DEFAULT_SESSION_ID, { limit: 50, before: auditCursor });
      setAudit((prev) => [...prev, ...page.entries]);
      setAuditCursor(page.nextBefore);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, [auditCursor]);

  useEffect(() => {
    refresh();
    loadInitialAudit();
    // Load character roster for pool scope picker (fire-and-forget).
    getCharacters(DEFAULT_SESSION_ID)
      .then((list) => {
        setCharRoster(
          list.characters.map((b) => ({
            id: b.id,
            name: (b.profile as { name?: string } | null)?.name ?? b.id,
          })),
        );
        // Build sub → display name map for resolving UUIDs in audit entries.
        const nameMap = new Map<string, string>();
        for (const b of list.characters) {
          if (b.ownerSub && b.ownerUsername) {
            nameMap.set(b.ownerSub, b.ownerUsername);
          }
        }
        setPlayerNames(nameMap);
      })
      .catch(() => {
        /* best-effort — scope picker degrades to party-only */
      });
  }, [refresh, loadInitialAudit]);

  // Open the live socket once on mount. The party page subscribes via the
  // same scene/subscribe path as Sheet so the connection row gets its
  // SessionId bound — that's what makes the BySession GSI fan-out reach us.
  useEffect(() => {
    let cancelled = false;
    let cs: CampaignSocket | null = null;

    (async () => {
      try {
        cs = await CampaignSocket.connect({
          sessionId: DEFAULT_SESSION_ID,
          mapId: DEFAULT_MAP_ID,
        });
        if (cancelled) {
          cs.close();
          return;
        }
        socketRef.current = cs;

        cs.on<PartyPatchPayload>(PartyServerTypes.PartyPatch, (payload) => {
          setSnapshot((prev) => {
            if (!prev) return prev;
            // Out-of-order echo guard. Shouldn't happen on a single
            // connection but keeps us safe under reconnects.
            if (payload.version <= prev.party.version) return prev;
            pendingVersionRef.current = payload.version;
            return { ...prev, party: payload.party };
          });
          setConflict(null);
          // Refresh the audit log so the new entry shows up. Cheap — small
          // page, single round trip — and avoids us reproducing the audit
          // composer client-side.
          loadInitialAudit();
        });

        cs.on<PartyRejectedPayload>(PartyServerTypes.PartyRejected, (payload) => {
          if (payload.party) {
            setSnapshot((prev) =>
              prev ? { ...prev, party: payload.party as PartyDoc } : prev,
            );
            pendingVersionRef.current = payload.version;
          }
          setConflict(`Rejected: ${payload.reason}. Reconciled to v${payload.version}.`);
        });

        // Loot broadcasts apply directly to snapshot.loot. Each broadcast is
        // independent — there's no version on LootItem in this slice — so we
        // key by id and overwrite-or-insert without any ordering gymnastics.
        // Audit gets refreshed on every mutation so the activity log stays
        // in step without us rebuilding audit composition client-side.
        cs.on<LootRowPayload>(LootServerTypes.LootAdded, (payload) => {
          setSnapshot((prev) => {
            if (!prev) return prev;
            // Deduplicate on id — the originator already applied the add
            // optimistically and the echo would otherwise double-insert.
            const without = prev.loot.filter((x) => x.id !== payload.item.id);
            return { ...prev, loot: [...without, payload.item] };
          });
          setLootError(null);
          loadInitialAudit();
        });

        cs.on<LootRowPayload>(LootServerTypes.LootUpdated, (payload) => {
          setSnapshot((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              loot: prev.loot.map((x) =>
                x.id === payload.item.id ? payload.item : x,
              ),
            };
          });
          setLootError(null);
          loadInitialAudit();
        });

        cs.on<LootRemovedPayload>(LootServerTypes.LootRemoved, (payload) => {
          setSnapshot((prev) => {
            if (!prev) return prev;
            return { ...prev, loot: prev.loot.filter((x) => x.id !== payload.id) };
          });
          setLootError(null);
          loadInitialAudit();
        });

        cs.on<LootRejectedPayload>(LootServerTypes.LootRejected, (payload) => {
          // On a rejection the originator's optimistic apply is wrong. Easiest
          // fix is a full re-read of the party snapshot — the loot list is
          // small and a single HTTP call will put us back in sync.
          setLootError(`Loot rejected: ${payload.reason}`);
          refresh();
        });
      } catch (err) {
        if (!cancelled) {
          // Live updates are nice-to-have on the party page; the HTTP read
          // path already populated the view. Surface the failure quietly.
          setConflict(
            err instanceof Error
              ? `Live updates offline: ${err.message}`
              : 'Live updates offline',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, [loadInitialAudit]);

  // Wallet/pool edits ship the FULL new party doc under a version check.
  // The client computes the new doc locally (apply the intended delta on top
  // of the current snapshot), the server overwrites under a conditional
  // PutItem, and the echoed party/patch confirms — same shape as Sheet's
  // slot updates. See PartyUpdateRequest for why we don't ship per-field
  // deltas.
  const sendPartyUpdate = useCallback(
    (next: { wallet: Wallet; pools: BudgetPool[] }, reason?: string) => {
      const cs = socketRef.current;
      const cur = snapshot;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: PartyUpdateRequest = {
        action: PartyActions.Update,
        expectedVersion: expected,
        wallet: next.wallet,
        pools: next.pools,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      // Optimistic local apply.
      setSnapshot((prev) =>
        prev
          ? {
              ...prev,
              party: {
                ...prev.party,
                version: expected + 1,
                wallet: next.wallet,
                pools: next.pools,
              },
            }
          : prev,
      );
    },
    [snapshot],
  );

  // Loot mutations ship the full intended row (or just the id for remove).
  // No optimistic apply on add because the id is server-generated; update
  // and remove apply locally immediately so the UI feels responsive, and the
  // broadcast echo reconciles. Rejections trigger a full refresh — see the
  // LootRejected listener above.
  const sendLootAdd = useCallback(
    (fields: Omit<LootAddRequest, 'action'>) => {
      const cs = socketRef.current;
      if (!cs) return;
      const req: LootAddRequest = { action: LootActions.Add, ...fields };
      cs.send(req);
    },
    [],
  );

  const sendLootUpdate = useCallback(
    (fields: Omit<LootUpdateRequest, 'action'>) => {
      const cs = socketRef.current;
      if (!cs) return;
      const req: LootUpdateRequest = { action: LootActions.Update, ...fields };
      cs.send(req);
      // Optimistic local apply so the edit form closes and the table updates
      // immediately. The echoed loot/updated confirms; a rejection refreshes.
      setSnapshot((prev) =>
        prev
          ? {
              ...prev,
              loot: prev.loot.map((x) =>
                x.id === fields.id
                  ? {
                      ...x,
                      name: fields.name,
                      quantity: fields.quantity,
                      weight: fields.weight,
                      notes: fields.notes,
                      source: fields.source,
                      identified: fields.identified,
                      tags: fields.tags,
                    }
                  : x,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const sendLootRemove = useCallback(
    (id: string, reason?: string) => {
      const cs = socketRef.current;
      if (!cs) return;
      const req: LootRemoveRequest = { action: LootActions.Remove, id, reason };
      cs.send(req);
      setSnapshot((prev) =>
        prev ? { ...prev, loot: prev.loot.filter((x) => x.id !== id) } : prev,
      );
    },
    [],
  );

  // Pool mutations piggyback on party/update — they all compute the new
  // pools array locally and ship the full replace. This keeps the wire
  // protocol tiny (no per-op messages) and the server stays the single
  // arbiter of invariants via PartyUpdateHandler.ValidateInvariants.
  const sendCreatePool = useCallback(
    (fields: { name: string; description?: string; targetAmount?: Wallet; ownerScope: string }) => {
      if (!snapshot) return;
      const pool: BudgetPool = {
        id:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? (crypto as Crypto).randomUUID().replace(/-/g, '')
            : Math.random().toString(16).slice(2) + Date.now().toString(16),
        name: fields.name,
        description: fields.description,
        currentAmount: { ...ZERO_WALLET },
        targetAmount: fields.targetAmount,
        ownerScope: fields.ownerScope,
        archived: false,
        createdAt: new Date().toISOString(),
      };
      sendPartyUpdate(
        { wallet: snapshot.party.wallet, pools: [...snapshot.party.pools, pool] },
        `create pool '${pool.name}'`,
      );
      setCreatingPool(false);
    },
    [snapshot, sendPartyUpdate],
  );

  const sendUpdatePoolMeta = useCallback(
    (
      id: string,
      fields: { name: string; description?: string; targetAmount?: Wallet; ownerScope: string },
    ) => {
      if (!snapshot) return;
      const nextPools = snapshot.party.pools.map((p) =>
        p.id === id
          ? {
              ...p,
              name: fields.name,
              description: fields.description,
              targetAmount: fields.targetAmount,
              ownerScope: fields.ownerScope,
            }
          : p,
      );
      sendPartyUpdate(
        { wallet: snapshot.party.wallet, pools: nextPools },
        `edit pool '${fields.name}'`,
      );
      setEditingPoolId(null);
    },
    [snapshot, sendPartyUpdate],
  );

  // Fund = reserve from unallocated into the pool. Wallet stays the same
  // (total coin the party holds doesn't change); the pool's currentAmount
  // grows. The server-side walletCovers(wallet, sum(pools)) invariant is
  // what actually blocks over-allocation — we mirror the guard here for a
  // cleaner UX, but the truth is server-side.
  const sendFundPool = useCallback(
    (id: string, amount: Wallet, reason?: string) => {
      if (!snapshot) return;
      const pool = snapshot.party.pools.find((p) => p.id === id);
      if (!pool) return;
      const nextPool: BudgetPool = {
        ...pool,
        currentAmount: addWallets(pool.currentAmount, amount),
      };
      const nextPools = snapshot.party.pools.map((p) => (p.id === id ? nextPool : p));
      // Local guard: would the resulting pool total exceed the wallet?
      const totalAllocated = nextPools
        .filter((p) => !p.archived)
        .reduce((acc, p) => addWallets(acc, p.currentAmount), ZERO_WALLET);
      if (!walletCovers(snapshot.party.wallet, totalAllocated)) {
        setConflict('Not enough unallocated coin to fund this pool.');
        return;
      }
      sendPartyUpdate(
        { wallet: snapshot.party.wallet, pools: nextPools },
        reason ?? `fund '${pool.name}'`,
      );
      setFundingPoolId(null);
    },
    [snapshot, sendPartyUpdate],
  );

  // Spend = money leaves both the pool and the party wallet. Needs the
  // pool to cover the spend — otherwise the UX would let you move the pool
  // negative and get a server rejection.
  const sendSpendPool = useCallback(
    (id: string, amount: Wallet, reason?: string) => {
      if (!snapshot) return;
      const pool = snapshot.party.pools.find((p) => p.id === id);
      if (!pool) return;
      if (!walletCovers(pool.currentAmount, amount)) {
        setConflict(`Pool '${pool.name}' doesn't have that much to spend.`);
        return;
      }
      const nextPool: BudgetPool = {
        ...pool,
        currentAmount: subWallets(pool.currentAmount, amount),
      };
      const nextPools = snapshot.party.pools.map((p) => (p.id === id ? nextPool : p));
      const nextWallet = subWallets(snapshot.party.wallet, amount);
      sendPartyUpdate(
        { wallet: nextWallet, pools: nextPools },
        reason ?? `spend from '${pool.name}'`,
      );
      setSpendingPoolId(null);
    },
    [snapshot, sendPartyUpdate],
  );

  // Toggling archive keeps the pool around (balance and all) but drops it
  // from the unallocated calc — see unallocatedWallet in lib/party.
  const sendToggleArchive = useCallback(
    (id: string) => {
      if (!snapshot) return;
      const pool = snapshot.party.pools.find((p) => p.id === id);
      if (!pool) return;
      const nextPools = snapshot.party.pools.map((p) =>
        p.id === id ? { ...p, archived: !p.archived } : p,
      );
      sendPartyUpdate(
        { wallet: snapshot.party.wallet, pools: nextPools },
        pool.archived ? `unarchive '${pool.name}'` : `archive '${pool.name}'`,
      );
    },
    [snapshot, sendPartyUpdate],
  );

  // Deleting a pool returns its current balance to unallocated — the
  // wallet total is unchanged, we just drop the row so the pool no longer
  // reserves against it.
  const sendDeletePool = useCallback(
    (id: string) => {
      if (!snapshot) return;
      const pool = snapshot.party.pools.find((p) => p.id === id);
      if (!pool) return;
      const nextPools = snapshot.party.pools.filter((p) => p.id !== id);
      sendPartyUpdate(
        { wallet: snapshot.party.wallet, pools: nextPools },
        `delete pool '${pool.name}'`,
      );
    },
    [snapshot, sendPartyUpdate],
  );

  if (loading) {
    return <p>Loading party…</p>;
  }
  if (error) {
    return <p style={{ color: 'var(--danger, #e06c75)' }}>Error: {error}</p>;
  }
  if (!snapshot) {
    return <p>No party data.</p>;
  }

  const { party, loot } = snapshot;
  const unallocated = unallocatedWallet(party);
  const activePools = party.pools.filter((p) => !p.archived);
  const archivedPools = party.pools.filter((p) => p.archived);

  return (
    <div style={{ maxWidth: '900px' }}>
      <h1>Party</h1>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Wallet</h2>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ color: 'var(--fg-muted)', marginRight: '0.5rem' }}>Total:</span>
          <WalletLine wallet={party.wallet} />
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{ color: 'var(--fg-muted)', marginRight: '0.5rem' }}>Unallocated:</span>
          <WalletLine wallet={unallocated} />
        </div>
        {conflict && (
          <p style={{ color: 'var(--danger, #e06c75)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
            {conflict}
          </p>
        )}
        <WalletEditor
          wallet={party.wallet}
          onSubmit={(nextWallet, reason) =>
            sendPartyUpdate({ wallet: nextWallet, pools: party.pools }, reason)
          }
        />
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Budget pools</h2>
        {activePools.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>No active pools.</p>
        )}
        {activePools.map((p) => (
          <div key={p.id}>
            <PoolRow
              pool={p}
              onEdit={(id) => {
                closePoolForms();
                setEditingPoolId(id);
              }}
              onFund={(id) => {
                closePoolForms();
                setFundingPoolId(id);
              }}
              onSpend={(id) => {
                closePoolForms();
                setSpendingPoolId(id);
              }}
              onArchiveToggle={sendToggleArchive}
              onDelete={sendDeletePool}
            />
            {editingPoolId === p.id && (
              <PoolMetaForm
                mode="edit"
                initial={p}
                characters={charRoster}
                onCancel={() => setEditingPoolId(null)}
                onSubmit={(fields) => sendUpdatePoolMeta(p.id, fields)}
              />
            )}
            {fundingPoolId === p.id && (
              <PoolCoinForm
                verb="Fund"
                onCancel={() => setFundingPoolId(null)}
                onSubmit={(amount, reason) => sendFundPool(p.id, amount, reason)}
              />
            )}
            {spendingPoolId === p.id && (
              <PoolCoinForm
                verb="Spend"
                onCancel={() => setSpendingPoolId(null)}
                onSubmit={(amount, reason) => sendSpendPool(p.id, amount, reason)}
              />
            )}
          </div>
        ))}
        <div style={{ marginTop: '0.75rem' }}>
          {creatingPool ? (
            <PoolMetaForm
              mode="create"
              characters={charRoster}
              onCancel={() => setCreatingPool(false)}
              onSubmit={sendCreatePool}
            />
          ) : (
            <button
              onClick={() => {
                closePoolForms();
                setCreatingPool(true);
              }}
            >
              + Create pool
            </button>
          )}
        </div>
        {archivedPools.length > 0 && (
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--fg-muted)' }}>
              Archived ({archivedPools.length})
            </summary>
            <div style={{ marginTop: '0.5rem' }}>
              {archivedPools.map((p) => (
                <div key={p.id}>
                  <PoolRow
                    pool={p}
                    onEdit={(id) => {
                      closePoolForms();
                      setEditingPoolId(id);
                    }}
                    onFund={() => {}}
                    onSpend={() => {}}
                    onArchiveToggle={sendToggleArchive}
                    onDelete={sendDeletePool}
                  />
                  {editingPoolId === p.id && (
                    <PoolMetaForm
                      mode="edit"
                      initial={p}
                      characters={charRoster}
                      onCancel={() => setEditingPoolId(null)}
                      onSubmit={(fields) => sendUpdatePoolMeta(p.id, fields)}
                    />
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </section>

      <ActivitySection
        audit={audit}
        auditLoading={auditLoading}
        auditError={auditError}
        auditCursor={auditCursor}
        loadOlderAudit={loadOlderAudit}
        playerNames={playerNames}
      />

      <section>
        <h2>Loot ({loot.length})</h2>
        {lootError && (
          <p style={{ color: 'var(--danger, #e06c75)', fontSize: '0.85rem' }}>
            {lootError}
          </p>
        )}
        {loot.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)' }}>No loot yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th>Item</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Source</th>
                <th>Tags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loot.map((item) =>
                editingLootId === item.id ? (
                  <tr key={item.id}>
                    <td colSpan={5}>
                      <LootForm
                        mode="edit"
                        initial={item}
                        onCancel={() => setEditingLootId(null)}
                        onSubmit={(fields) => {
                          sendLootUpdate({ id: item.id, ...fields });
                          setEditingLootId(null);
                        }}
                      />
                    </td>
                  </tr>
                ) : (
                  <LootRow
                    key={item.id}
                    item={item}
                    onEdit={(id) => setEditingLootId(id)}
                    onRemove={(id) => sendLootRemove(id)}
                  />
                ),
              )}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ fontSize: '1rem', margin: '0 0 0.25rem' }}>Add loot</h3>
          <LootForm mode="add" onSubmit={(fields) => sendLootAdd(fields)} />
        </div>
      </section>
    </div>
  );
}

// ---------- Activity section with filter chips + UUID resolution ----------

/** UUID v4 pattern — matches Cognito subs stored before the email fallback fix. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Activity action categories for filter chips. */
const ACTIVITY_FILTERS: { label: string; actions: string[] }[] = [
  { label: 'HP',          actions: ['character/hp'] },
  { label: 'Conditions',  actions: ['character/conditions'] },
  { label: 'Dice Rolls',  actions: ['session/roll'] },
  { label: 'Loot',        actions: ['loot/add', 'loot/update', 'loot/remove'] },
  { label: 'Wallet',      actions: ['party/update'] },
  { label: 'Inventory',   actions: ['character/inventory'] },
  { label: 'Slots',       actions: ['character/slots'] },
  { label: 'Resources',   actions: ['character/resources'] },
  { label: 'Inspiration', actions: ['character/inspiration'] },
  { label: 'Hit Dice',    actions: ['character/hit-dice'] },
];

function ActivitySection({
  audit,
  auditLoading,
  auditError,
  auditCursor,
  loadOlderAudit,
  playerNames,
}: {
  audit: AuditEntry[];
  auditLoading: boolean;
  auditError: string | null;
  auditCursor: string | null;
  loadOlderAudit: () => void;
  playerNames: Map<string, string>;
}) {
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  const toggleFilter = (label: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Resolve a possibly-UUID actorUsername to a human-readable display name.
  const displayName = (username: string): string => {
    if (UUID_RE.test(username)) {
      return playerNames.get(username) ?? username.slice(0, 8) + '…';
    }
    return username;
  };

  // Filter audit entries when chips are active.
  const filtered = activeFilters.size === 0
    ? audit
    : audit.filter((e) => {
        for (const label of activeFilters) {
          const group = ACTIVITY_FILTERS.find((f) => f.label === label);
          if (group && group.actions.includes(e.action)) return true;
        }
        return false;
      });

  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2>Activity</h2>

      {/* Filter chips */}
      <div className="chip-row" style={{ marginBottom: '0.5rem' }}>
        {ACTIVITY_FILTERS.map((f) => {
          const active = activeFilters.has(f.label);
          return (
            <button
              key={f.label}
              onClick={() => toggleFilter(f.label)}
              style={{
                padding: '0.2rem 0.6rem',
                borderRadius: '999px',
                fontSize: '0.8rem',
                background: active ? 'var(--bg-elevated)' : 'transparent',
                border: `1px solid ${active ? 'var(--sheet-a, #f5c77a)' : 'var(--border)'}`,
                color: active ? 'var(--sheet-a, #f5c77a)' : 'var(--fg-muted)',
                cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          );
        })}
        {activeFilters.size > 0 && (
          <button
            onClick={() => setActiveFilters(new Set())}
            style={{
              padding: '0.2rem 0.6rem',
              borderRadius: '999px',
              fontSize: '0.8rem',
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--fg-muted)',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
      </div>

      {auditError && (
        <p style={{ color: 'var(--danger, #e06c75)' }}>Audit error: {auditError}</p>
      )}
      {filtered.length === 0 && !auditLoading && !auditError && (
        <p style={{ color: 'var(--fg-muted)' }}>
          {audit.length === 0 ? 'No activity yet.' : 'No entries match the current filters.'}
        </p>
      )}
      {filtered.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {filtered.map((e) => (
            <li
              key={e.id}
              style={{
                padding: '0.5rem 0',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'baseline',
              }}
            >
              <span className="num" style={{ color: 'var(--fg-muted)', fontSize: '0.85rem', minWidth: '11rem' }}>
                {new Date(e.ts).toLocaleString()}
              </span>
              <span style={{ color: 'var(--fg-muted)', minWidth: '7rem' }}>
                {displayName(e.actorUsername)}
              </span>
              <span style={{ flex: 1 }}>
                {e.summary}
                {e.reason && (
                  <span style={{ color: 'var(--fg-muted)', marginLeft: '0.5rem' }}>
                    ({e.reason})
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {auditCursor && (
        <button
          onClick={loadOlderAudit}
          disabled={auditLoading}
          style={{ marginTop: '0.75rem' }}
        >
          {auditLoading ? 'Loading…' : 'Load older'}
        </button>
      )}
    </section>
  );
}
